import React from 'react';
import { AlertTriangleIcon, CheckCircle2Icon } from 'lucide-react';
import { Input } from './ui/Input';
import { Button } from './ui/Button';
import { useContactForm } from '../hooks/useContactForm';
import type { ContactPayload } from '../utils/contactSubmit';
import { contactEmail, privacyUrl } from '../data/siteLinks';
interface ContactFormProps {
  idPrefix: string;
  source: ContactPayload['source'];
  submitLabel: string;
  requireMessage?: boolean;
  messageLabel?: string;
}
export function ContactForm({
  idPrefix,
  source,
  submitLabel,
  requireMessage = true,
  messageLabel = 'Čo chcete postaviť?'
}: ContactFormProps) {
  const {
    fields,
    errors,
    status,
    updateField,
    handleSubmit,
    reset
  } = useContactForm({
    idPrefix,
    source,
    requireMessage
  });
  if (status === 'success') {
    return <div role="status" className="text-center py-10 px-4" data-id="element-23" data-mp="3pihaq8nc5zuj">
        <div className="w-14 h-14 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-5" data-id="element-24" data-mp="3kkpgh7z7y1n6">
          <CheckCircle2Icon className="w-7 h-7 text-emerald-600" aria-hidden="true" data-id="element-25" data-mp="3ploolb3jsziw" />
        </div>
        <h3 className="text-2xl font-bold text-apple-black mb-3" data-id="element-26" data-mp="3ujq6vtt5gutb">Ďakujeme, správa je odoslaná</h3>
        <p className="text-apple-gray mb-8 max-w-sm mx-auto" data-id="element-27" data-mp="352bm5zcuiw3">
          Ozveme sa vám do 24 hodín na zadaný e-mail.
        </p>
        <button type="button" onClick={reset} className="min-h-[44px] px-5 text-sm font-semibold text-apple-blue hover:underline" data-id="element-28" data-mp="83dl2flqmrds">
          Poslať ďalšiu správu
        </button>
      </div>;
  }
  const messageId = `${idPrefix}-message`;
  const consentId = `${idPrefix}-consent`;
  return <form noValidate onSubmit={handleSubmit} className="space-y-5 text-left" aria-busy={status === 'submitting'} data-id="element-29" data-mp="3gml7gcnxyu71">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-30" data-mp="vu9lg149euwn">
        <Input id={`${idPrefix}-name`} label="Meno a priezvisko" placeholder="Jozef Mrkvička" autoComplete="name" value={fields.name} onChange={e => updateField('name', e.target.value)} error={errors.name} aria-invalid={!!errors.name} required data-id="element-31" data-mp="vtq0scn85sex" />
        <Input id={`${idPrefix}-email`} label="E-mail" type="email" placeholder="jozef@firma.sk" autoComplete="email" value={fields.email} onChange={e => updateField('email', e.target.value)} error={errors.email} aria-invalid={!!errors.email} required data-id="element-32" data-mp="35wx7d6r9co0i" />
      </div>
      <Input id={`${idPrefix}-phone`} label="Telefón (voliteľné)" type="tel" placeholder="+421 900 000 000" autoComplete="tel" value={fields.phone} onChange={e => updateField('phone', e.target.value)} error={errors.phone} aria-invalid={!!errors.phone} data-id="element-33" data-mp="294toabipds8o" />
      <div data-id="element-34" data-mp="1pjgs3k9g60uz">
        <label htmlFor={messageId} className="block text-sm font-medium text-apple-black mb-1.5" data-id="element-35" data-mp="1pjp4zc5kl666">
          {messageLabel}
          {!requireMessage && ' (voliteľné)'}
        </label>
        <textarea id={messageId} rows={4} value={fields.message} onChange={e => updateField('message', e.target.value)} aria-invalid={!!errors.message} aria-describedby={errors.message ? `${messageId}-error` : undefined} placeholder="Stručne popíšte nápad, cieľ alebo problém, ktorý chcete vyriešiť." className={`w-full rounded-lg border bg-white px-3.5 py-3 text-base text-apple-black placeholder:text-gray-500 transition-colors focus:outline-none focus:ring-2 focus:ring-apple-blue/30 ${errors.message ? 'border-red-500' : 'border-gray-300 focus:border-apple-blue'}`} data-id="element-36" data-mp="9s4sr185fr1a" />
        {errors.message && <p id={`${messageId}-error`} className="mt-1.5 text-sm text-red-600" data-id="element-37" data-mp="2zfvoptmpx22t">
            {errors.message}
          </p>}
      </div>

      <div data-id="element-38" data-mp="34dx7edg8ezdr">
        <label htmlFor={consentId} className="flex items-start gap-3 text-sm text-apple-gray cursor-pointer min-h-[44px] py-1" data-id="element-39" data-mp="3jpn31ataynh5">
          <input id={consentId} type="checkbox" checked={fields.consent} onChange={e => updateField('consent', e.target.checked)} aria-invalid={!!errors.consent} aria-describedby={errors.consent ? `${consentId}-error` : undefined} className="mt-0.5 h-5 w-5 flex-shrink-0 rounded accent-apple-blue" data-id="element-40" data-mp="3eyoks5or7fb9" />
          <span data-id="element-41" data-mp="2h06uxfm4alp">
            Súhlasím s{' '}
            <a href={privacyUrl} target="_blank" rel="noopener noreferrer" className="font-medium text-apple-black underline underline-offset-2 hover:text-apple-blue" data-id="element-42" data-mp="2ib5yjy44u6h">
              podmienkami ochrany súkromia
            </a>
            .
          </span>
        </label>
        {errors.consent && <p id={`${consentId}-error`} className="mt-1 text-sm text-red-600" data-id="element-43" data-mp="2d8jf4fjke237">
            {errors.consent}
          </p>}
      </div>

      {status === 'error' && <div role="alert" className="flex items-start gap-2.5 bg-red-50 border border-red-100 text-red-700 text-sm font-medium rounded-xl p-4" data-id="element-44" data-mp="2847jnna45zuw">
          <AlertTriangleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-45" data-mp="2d290e1k2hn37" />
          <span data-id="element-46" data-mp="2hw3jb2ruly18">
            Odoslanie zlyhalo. Skúste to znova alebo napíšte priamo na{' '}
            <a href={`mailto:${contactEmail}`} className="underline" data-id="element-47" data-mp="2lvcdg8qsvxxb">
              {contactEmail}
            </a>
            .
          </span>
        </div>}

      <Button type="submit" size="large" loading={status === 'submitting'} className="w-full !min-h-[52px] !bg-apple-blue hover:!bg-apple-blueHover !text-white !rounded-full !font-semibold !border-none" data-id="element-48" data-mp="2vts64g5h7f8x">
        {submitLabel}
      </Button>
    </form>;
}